'use client';

import { useCallback, useMemo } from 'react';
import { VStack, HStack, Box } from '@terra/ui-styled-system';
import { Text, IconButton, Badge, Tooltip } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import {
  Loading03Icon,
  Minimize02Icon,
  SquareIcon,
  HierarchyIcon,
} from '@terra/ui-icons';
import type {
  InternalCrownJewelDto,
  ScenarioDto,
} from '@terra/shared-contracts-internal-network';
import type { SignalDto } from '@terra/shared-contracts-signals';
import {
  InternalNetworkActionClassSchema,
  ScenarioStatusSchema,
} from '@terra/shared-schemas-orchestrator';
import { humanizeSnakeCase } from './string.utils';
import { ScenarioAttackPathGraph } from './ScenarioAttackPathGraph';
import { ScenarioStepsTimeline } from './ScenarioStepsTimeline';
import { buildScenarioStepTimelineItemsFromScenario } from './scenario-step-timeline.util';

type InternalNetworkScenarioPanelProps = {
  scenario: ScenarioDto;
  networkId: string;
  signal?: SignalDto;
  attackPathCenterNodeId?: string | null;
  isHighlighted?: boolean;
  canStop?: boolean;
  isStopPending?: boolean;
  isAttackPathExpanded?: boolean;
  onStop?: () => void;
  onToggleAttackPath?: () => void;
  panelRef?: (element: HTMLDivElement | null) => void;
  crownJewels?: InternalCrownJewelDto[];
  showDivider?: boolean;
};

export function InternalNetworkScenarioCard({
  scenario,
  networkId,
  signal,
  attackPathCenterNodeId = null,
  isHighlighted = false,
  canStop = false,
  isStopPending = false,
  isAttackPathExpanded = false,
  onStop,
  onToggleAttackPath,
  panelRef,
  crownJewels = [],
  showDivider = false,
}: InternalNetworkScenarioPanelProps) {
  const t = useTranslations('internalNetwork.scenarios');
  const tDiscovery = useTranslations('internalNetwork.scenarios.discovery');
  const tExecutionTier = useTranslations('internalNetwork.executionTier');
  const tActionClass = useTranslations('internalNetwork.scenarios.actionClass');
  const tStatus = useTranslations('internalNetwork.scenarios.status');
  const isDiscoveryScenario = scenario.actionClass === 'DISCOVERY';
  const formatActionClass = (actionClass: string) => {
    const parsed = InternalNetworkActionClassSchema.safeParse(actionClass);

    return parsed.success ? tActionClass(parsed.data) : actionClass;
  };

  const formatStatus = (status: string) => {
    const parsed = ScenarioStatusSchema.safeParse(status);

    return parsed.success ? tStatus(parsed.data) : status;
  };

  const formatStepAction = useCallback(
    (action: string) => humanizeSnakeCase(action),
    [],
  );
  const timelineItems = useMemo(
    () =>
      buildScenarioStepTimelineItemsFromScenario({
        scenario,
        formatAction: formatStepAction,
      }),
    [formatStepAction, scenario],
  );
  const hasTimelineHops = (scenario.typedTaskPlan?.steps.length ?? 0) > 0;
  const canShowNeighborhoodAttackPath =
    attackPathCenterNodeId !== null &&
    signal !== undefined &&
    (!isDiscoveryScenario || Boolean(scenario.crownJewelId));
  const canShowAttackPath = hasTimelineHops || canShowNeighborhoodAttackPath;
  const showGuardrailSummary =
    scenario.guardrailPrecheckResult &&
    !scenario.guardrailPrecheckResult.allowed;
  const linkedCrownJewel = scenario.crownJewelId
    ? crownJewels.find((jewel) => jewel.id === scenario.crownJewelId)
    : undefined;

  return (
    <Box
      ref={panelRef}
      w="full"
      borderTopWidth={showDivider ? '1px' : undefined}
      borderTopColor={showDivider ? 'border.neutral.subtle' : undefined}
      pt={showDivider ? 4 : undefined}
      borderLeftWidth={isHighlighted ? '2px' : undefined}
      borderLeftColor={isHighlighted ? 'accent.default' : undefined}
      pl={isHighlighted ? 3 : undefined}
    >
      <VStack alignItems="stretch" gap={2} w="full">
        <HStack
          gap={2}
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
          w="full"
        >
          <HStack gap={2} flexWrap="wrap" alignItems="center" minW={0}>
            <Text fontSize="sm" fontWeight="medium">
              {formatActionClass(scenario.actionClass)}
            </Text>
            <Badge variant="outline" size="sm">
              {formatStatus(scenario.status)}
            </Badge>
            <Badge variant="outline" size="sm">
              {tExecutionTier(scenario.executionTier)}
            </Badge>
          </HStack>

          <HStack gap={1} flexShrink={0}>
            {canShowAttackPath && onToggleAttackPath ? (
              <Tooltip
                positioning={{ strategy: 'fixed' }}
                title={
                  isAttackPathExpanded
                    ? t('hideAttackPath')
                    : t('viewAttackPath')
                }
              >
                <IconButton
                  size="xs"
                  variant="ghost"
                  aria-label={
                    isAttackPathExpanded
                      ? t('hideAttackPath')
                      : t('viewAttackPath')
                  }
                  onClick={onToggleAttackPath}
                >
                  {isAttackPathExpanded ? (
                    <Minimize02Icon size={14} />
                  ) : (
                    <HierarchyIcon size={14} />
                  )}
                </IconButton>
              </Tooltip>
            ) : null}
            {canStop && onStop ? (
              <Tooltip positioning={{ strategy: 'fixed' }} title={t('stop')}>
                <IconButton
                  size="xs"
                  variant="ghost"
                  aria-label={t('stop')}
                  disabled={isStopPending}
                  onClick={onStop}
                >
                  {isStopPending ? (
                    <Loading03Icon
                      size={14}
                      style={{ animation: 'spin 1s linear infinite' }}
                    />
                  ) : (
                    <SquareIcon size={14} />
                  )}
                </IconButton>
              </Tooltip>
            ) : null}
          </HStack>
        </HStack>

        {linkedCrownJewel ? (
          <Text fontSize="xs" color="fg.muted">
            {linkedCrownJewel.label ?? linkedCrownJewel.entityId}
          </Text>
        ) : null}

        {isDiscoveryScenario ? (
          <Text fontSize="xs" color="fg.muted">
            {tDiscovery('validationLabel')}
          </Text>
        ) : null}

        {scenario.blastRadiusSummary ? (
          <Text fontSize="xs" color="fg.muted">
            {scenario.blastRadiusSummary}
          </Text>
        ) : null}

        {showGuardrailSummary ? (
          <Text fontSize="xs" color="fg.error">
            {t('guardrailPrecheck')}:{' '}
            {scenario.guardrailPrecheckResult?.blockedReason ??
              t('guardrailBlocked')}
          </Text>
        ) : null}

        {timelineItems.length > 0 ? (
          <ScenarioStepsTimeline items={timelineItems} />
        ) : null}

        {isAttackPathExpanded && canShowAttackPath ? (
          <ScenarioAttackPathGraph
            networkId={networkId}
            scenario={scenario}
            signal={signal}
            crownJewels={crownJewels}
          />
        ) : null}
      </VStack>
    </Box>
  );
}
